<template>
  <div class="relative w-full bg-surface-100 dark:bg-surface-800 rounded-lg overflow-hidden">
    <div class="relative w-full pt-[56.25%] overflow-hidden rounded-lg bg-surface-100 dark:bg-surface-800">
      <video ref="videoRef" class="absolute inset-0 w-full h-full object-cover bg-black -webkit-transform-z-0 transform-z-0"></video>
      <canvas ref="canvasRef" class="absolute inset-0 w-full h-full pointer-events-none object-cover -webkit-transform-z-0 transform-z-0"></canvas>
      <div class="absolute inset-0 flex justify-center items-end z-1 pointer-events-none">
        <div class="text-surface-0/70 text-sm text-center px-4 py-2 bg-black/50 rounded backdrop-blur-sm mb-4">
          <div>{{ scannedCode }}</div>
        </div>
      </div>
    </div>
    <div class="flex gap-4 justify-center px-4 mt-4">
      <Button label="카메라 전환" class="p-button-outlined flex-1 max-w-[200px] text-sm" @click="switchCamera" />
      <Button label="재시도" class="p-button-outlined flex-1 max-w-[200px] text-sm" @click="retryScan" />
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, watch, nextTick } from 'vue'
import jsQR from 'jsqr'
import Button from 'primevue/button'

const emit = defineEmits(['scanned'])
const props = defineProps({
  modelValue: String // 필요시 v-model로 외부 제어
})

const scanning = ref(true)
const camera = ref('environment')
const videoRef = ref(null)
const canvasRef = ref(null)
const stream = ref(null)
let animationFrameId = null
const scannedCode = ref('')
const errorMessage = ref('')

const startScanner = async () => {
  try {
    if (stream.value) {
      stream.value.getTracks().forEach(track => track.stop())
    }
    const constraints = {
      video: {
        facingMode: camera.value,
        width: { ideal: 1280 },
        height: { ideal: 720 },
        aspectRatio: 1.777777778
      }
    }
    // iOS 디바이스 체크
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    if (isIOS) {
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    stream.value = await navigator.mediaDevices.getUserMedia(constraints)
    videoRef.value.srcObject = stream.value
    videoRef.value.setAttribute('playsinline', '')
    videoRef.value.setAttribute('webkit-playsinline', '')
    await new Promise((resolve) => {
      videoRef.value.onloadedmetadata = () => {
        resolve();
      };
    });
    await videoRef.value.play()
    if (camera.value === 'user') {
      videoRef.value.style.transform = 'scaleX(-1)'
      canvasRef.value.style.transform = 'scaleX(-1)'
    } else {
      videoRef.value.style.transform = 'none'
      canvasRef.value.style.transform = 'none'
    }
    scanning.value = true
    errorMessage.value = ''
    const tick = () => {
      if (!scanning.value) return
      if (videoRef.value.readyState === videoRef.value.HAVE_ENOUGH_DATA) {
        const canvas = canvasRef.value
        const context = canvas.getContext('2d')
        canvas.height = videoRef.value.videoHeight
        canvas.width = videoRef.value.videoWidth
        context.drawImage(videoRef.value, 0, 0, canvas.width, canvas.height)
        const imageData = context.getImageData(0, 0, canvas.width, canvas.height)
        const code = jsQR(imageData.data, imageData.width, imageData.height, {
          inversionAttempts: "dontInvert"
        })
        if (code && code.data) {
          context.fillStyle = "rgba(255, 59, 88, 0.2)"
          context.fillRect(
            code.location.topLeftCorner.x,
            code.location.topLeftCorner.y,
            code.location.topRightCorner.x - code.location.topLeftCorner.x,
            code.location.bottomLeftCorner.y - code.location.topLeftCorner.y
          )
          context.strokeStyle = "#FF3B58"
          context.lineWidth = 4
          context.beginPath()
          context.moveTo(code.location.topLeftCorner.x, code.location.topLeftCorner.y)
          context.lineTo(code.location.topRightCorner.x, code.location.topRightCorner.y)
          context.lineTo(code.location.bottomRightCorner.x, code.location.bottomRightCorner.y)
          context.lineTo(code.location.bottomLeftCorner.x, code.location.bottomLeftCorner.y)
          context.lineTo(code.location.topLeftCorner.x, code.location.topLeftCorner.y)
          context.stroke()          
          scannedCode.value = code.data
          stopScanner()
          console.log('emit scanned 이벤트 발생:', code.data)
          emit('scanned', code.data)
          return
        } else {
          scannedCode.value = 'QR코드 인식중...'
        }
      }
      animationFrameId = requestAnimationFrame(tick)
    }
    tick()
  } catch (error) {
    console.error('스캐너 초기화 실패:', error)
    errorMessage.value = '카메라 초기화에 실패했습니다.'
  }
}

const stopScanner = () => {
  if (stream.value) {
    stream.value.getTracks().forEach(track => track.stop())
  }
  if (animationFrameId) {
    cancelAnimationFrame(animationFrameId)
  }
  scanning.value = false
}

const switchCamera = async () => {
  camera.value = camera.value === 'environment' ? 'user' : 'environment'
  await startScanner()
}

const retryScan = async () => {
  await startScanner()
}

onMounted(async () => {
  await nextTick()
  await startScanner()
})

onUnmounted(() => {
  stopScanner()
})
</script> 